import { useMemo } from 'react';
import type { DSPopperJST } from '../react-desc-prop-types.js';

interface UseConfiguredDefaultModifiersT {
  props: DSPopperJST.Props;
  arrowElement: HTMLDivElement | null;
  defaultProps: Omit<DSPopperJST.DefaultProps, 'zIndex'>;
}

export const useConfiguredDefaultModifiers = ({
  props,
  arrowElement,
  defaultProps,
}: UseConfiguredDefaultModifiersT) => {
  // we destructure explicit app definitions before merge
  const {
    boundaryElement: explicitAppBoundaryElement,
    withoutArrow: explicitAppWithoutArrow = false,
    placementOrderPreference: explicitPlacementOrderPreference = ['top', 'bottom', 'left', 'right'],
    customOffset: explictAppOffset = undefined,
  } = props;
  // to build default modifiers on top of it
  const defaultModifiers = useMemo(() => {
    const mod = [];
    mod.push({ name: 'hide', enabled: true });
    mod.push({
      name: 'flip',
      options: {
        fallbackPlacements: explicitPlacementOrderPreference,
      },
    });
    // we apply an offset if the app defines one or if the app wants the arrow
    if (!explicitAppWithoutArrow || explictAppOffset) {
      const defaultOffset = explictAppOffset || defaultProps.customOffset;
      mod.push({
        name: 'offset',
        enabled: true,
        options: {
          offset: defaultOffset,
        },
      });
    }
    // the app can opt for a "no arrow" implementation (we do so in datatable/calendar etc...)
    if (!explicitAppWithoutArrow)
      mod.push({
        name: 'arrow',
        options: {
          element: arrowElement,
          padding: 18,
        },
      });

    // the app can define an explicit element to act as a boundary instead of popperjs default one
    if (explicitAppBoundaryElement)
      mod.push({
        name: 'preventOverflow',
        options: {
          boundary: explicitAppBoundaryElement,
        },
      });
    return mod;
  }, [
    arrowElement,
    defaultProps,
    explicitAppBoundaryElement,
    explicitAppWithoutArrow,
    explicitPlacementOrderPreference,
    explictAppOffset,
  ]);
  // if the app pass custom modifier, we don't use "default modifiers"
  const { modifiers: explicitAppModifiers = defaultModifiers } = props;

  return useMemo(
    () => ({
      modifiers: explicitAppModifiers,
    }),
    [explicitAppModifiers],
  );
};
